











































































































































import { Component, Prop } from 'vue-property-decorator';
import _ from 'lodash';

import AppVue from '@/AppVue.vue';
import {
  QuestionnaireModel,
  CriticalSkillModel,
  SkillsStrategyModel,
  QuestionnaireLookups,
  SimpleLookupModel,
  StrategyLookupModel,
} from '@/core/models';
import { InputFieldCpt, InputTextareaCpt, InputSelectWithIconButtonCpt, InputUploadCpt } from '@/core/components';
import AddNewMatchSkillDialogCpt from './AddNewMatchSkillDialogCpt.vue';
import { StrategyType } from '@/core/constants';

@Component({
  components: {
    InputFieldCpt,
    InputTextareaCpt,
    InputSelectWithIconButtonCpt,
    AddNewMatchSkillDialogCpt,
    InputUploadCpt,
  },
})
export default class QuestionnaireStep2Cpt extends AppVue {
  @Prop() lookups: QuestionnaireLookups;
  @Prop() model: QuestionnaireModel;
  @Prop({ default: false }) disabled: boolean;

  showAddMatchSkillDialog = false;
  currentSkill?: CriticalSkillModel = undefined;
  loadingSkills = false;

  private importSkills(file: any) {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      this.loadingSkills = true;
      try {
        const content = e.target.result;
        this.processSkillsFile(content);
      } catch {
        this.$notify({
          title: 'Something went wrong',
          message: 'We could not read the file. Please try again.',
          type: 'error',
        });
      } finally {
        this.loadingSkills = false;
      }
    };

    reader.onerror = () => {
      this.loadingSkills = false;
      this.$notify({
        title: 'Something went wrong',
        message: 'We could not read the file. Please try again.',
        type: 'error',
      });
    };

    reader.readAsText(file.file, 'UTF-8');
  }

  private showInvalidFileMessage() {
    this.$notify({
      title: 'Invalid file',
      message: 'Please upload a valid CSV file.',
      type: 'error',
    });
  }

  private processSkillsFile(content: string) {
    const lines = content.split(/\r\n|\n/).filter(line => line.trim() !== '');
    if (lines.length <= 1) {
      this.showInvalidFileMessage();
      return;
    }

    const headers = this.parseCSVLine(lines[0]);
    const areaIndex = headers.findIndex(h => h.toLowerCase() === 'area');
    const textIndex = headers.findIndex(h => h.toLowerCase() === 'text');

    if (areaIndex === -1 || textIndex === -1) {
      this.showInvalidFileMessage();
      return;
    }
    this.model.criticalSkills = [];
    for (let i = 1; i < lines.length && !this.maxSkillAmountReached; i++) {
      const values = this.parseCSVLine(lines[i]);

      if (values[areaIndex]?.trim() === 'TOP 10 Critical skills') {
        const skillText = values[textIndex]?.trim();
        if (skillText) {
          this.model.criticalSkills.push(
            new CriticalSkillModel({
              name: skillText,
              strategies1: this.createStrategies(StrategyType.Current),
              strategies2: this.createStrategies(StrategyType.New),
            }),
          );
        }
      }
    }

    this.$emit('skillsImported', this.model.criticalSkills);
  }

  private parseCSVLine(line: string): string[] {
    // Simple CSV parser - handles quoted fields with commas
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    // Add the last field
    result.push(current.trim());

    return result;
  }

  get noCriticalSkillsValidationMessage() {
    if (!this.model || !this.model.modelState) {
      return '';
    }

    const messages = this.model.modelState['criticalSkills'];
    if (!messages || messages.length === 0) {
      return '';
    }

    return messages[0] || '';
  }

  private getValidationMessageForSkill(index: number) {
    if (!this.model || !this.model.modelState) {
      return '';
    }

    const messages = this.model.modelState[`criticalSkills[${index}].name`];
    if (!messages || messages.length === 0) {
      return '';
    }

    return messages[0] || '';
  }

  private onChangeCriticalSkillName(index: number) {
    if (!this.model || !this.model.modelState) {
      return;
    }

    this.model.modelState[`criticalSkills[${index}].name`] = null as any;
  }

  created() {
    if (this.model.criticalSkills.length === 0) {
      this.addSkill();
    }
  }

  get maxSkillAmountReached() {
    return this.model.criticalSkills.length === 10;
  }

  matchSkillDialogClosed(result: SimpleLookupModel) {
    this.showAddMatchSkillDialog = false;
    if (result) {
      this.$eventHub.$emit('MATCH_SKILL_ADDED', result, this.currentSkill);
    }
    this.currentSkill = undefined;
  }

  addNewMatchSkill(skill: CriticalSkillModel) {
    this.currentSkill = skill;
    this.showAddMatchSkillDialog = true;
  }

  addSkill(name?: string) {
    if (this.model && this.model.modelState) {
      this.model.modelState['criticalSkills'] = null as any;
    }

    this.model.criticalSkills.push(
      new CriticalSkillModel({
        name,
        strategies1: this.createStrategies(StrategyType.Current),
        strategies2: this.createStrategies(StrategyType.New),
      }),
    );
  }

  goToSkill(index: number) {
    this.$eventHub.$emit('QNR_GO_TO_SUBSTEP', index);
  }

  deleteSkill(index: number) {
    if (confirm('Are you sure you want to delete this item?')) {
      this.model.criticalSkills.splice(index, 1);
    }
  }

  private createStrategies(strategyTypeId: number) {
    return _.flatMap(this.lookups.strategies, (strategy: StrategyLookupModel) => {
      return _.map(this.lookups.strategyLevels, (strategyLevel: SimpleLookupModel) => {
        return {
          strategyTypeId,
          strategyId: strategy.id,
          strategyLevelId: strategyLevel.id,
          isSelected: false,
        } as SkillsStrategyModel;
      });
    });
  }
}
